import { useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "@phosphor-icons/react";
import PageShell from "@/components/PageShell";
import PageMeta from "@/components/PageMeta";
import useJsonLd from "@/lib/useJsonLd";
import { findArticle, articles, categories } from "@/help/articles";

export default function HelpArticle() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const article = findArticle(slug);

  useEffect(() => {
    if (!article) return;
    document.title = `${article.title} — SendMe Help`;
    return () => { document.title = "SendMe — Share files with a 6-digit code"; };
  }, [article]);

  // TechArticle + Breadcrumb JSON-LD — helps AI assistants surface accurate
  // support answers when users ask questions like "how do I upload on SendMe".
  const cat = categories.find((c) => c.id === article?.category);
  const bodyText = article
    ? article.body.map((b) => b.text || (b.items ? b.items.join(" ") : "")).join("\n\n")
    : "";
  useJsonLd(
    "help-article",
    article
      ? {
          "@context": "https://schema.org",
          "@type": "TechArticle",
          headline: article.title,
          description: article.excerpt,
          articleSection: cat?.label || article.category,
          author: { "@type": "Organization", name: "SendMe", url: "https://sendme.co.in" },
          publisher: {
            "@type": "Organization",
            name: "SendMe",
            url: "https://sendme.co.in",
            logo: { "@type": "ImageObject", url: "https://sendme.co.in/logo192.png" },
          },
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id": `https://sendme.co.in/help/${article.slug}`,
          },
          articleBody: bodyText,
          wordCount: bodyText.trim().split(/\s+/).length,
          inLanguage: "en",
        }
      : null
  );
  useJsonLd(
    "help-breadcrumb",
    article
      ? {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://sendme.co.in/" },
            { "@type": "ListItem", position: 2, name: "Help", item: "https://sendme.co.in/help" },
            ...(cat ? [{ "@type": "ListItem", position: 3, name: cat.label, item: `https://sendme.co.in/help#${cat.id}` }] : []),
            { "@type": "ListItem", position: cat ? 4 : 3, name: article.title, item: `https://sendme.co.in/help/${article.slug}` },
          ],
        }
      : null
  );

  if (!article) {
    return (
      <PageShell eyebrow="404" title="Article not found." narrow>
        <Link to="/help" className="btn-brut-primary">← Back to help</Link>
      </PageShell>
    );
  }

  const category = categories.find((c) => c.id === article.category);
  const related = articles.filter((a) => a.category === article.category && a.slug !== slug).slice(0, 4);

  return (
    <article className="max-w-3xl mx-auto px-6 lg:px-10 py-10 md:py-14 fade-up" data-testid={`help-article-${slug}`}>
      <PageMeta
        title={article.title}
        description={article.excerpt}
        type="article"
      />
      <button
        onClick={() => navigate("/help")}
        className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-black/55 hover:text-[#FF3366] mb-6"
        data-testid="back-to-help"
      >
        <ArrowLeft size={14} /> All help articles
      </button>

      <div className="small-caps mb-3">
        Help · {category?.label || article.category}
      </div>
      <h1 className="text-3xl md:text-4xl lg:text-5xl tracking-tighter leading-[1.05] mb-4">{article.title}</h1>
      <p className="text-base md:text-lg text-black/65 leading-relaxed mb-8">{article.excerpt}</p>

      <div className="prose-content">
        {article.body.map((block, i) => <Block key={i} block={block} />)}
      </div>

      {related.length > 0 && (
        <div className="mt-14 border-t border-black/10 pt-8">
          <div className="small-caps mb-4">Related help articles</div>
          <ul className="grid sm:grid-cols-2 gap-3">
            {related.map((r) => (
              <li key={r.slug}>
                <Link
                  to={`/help/${r.slug}`}
                  className="block brut-card p-4 brut-hover"
                  data-testid={`related-${r.slug}`}
                >
                  <div className="font-bold leading-snug mb-1">{r.title}</div>
                  <div className="text-xs text-black/55 leading-snug">{r.excerpt}</div>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-12 border border-black p-6 md:p-8 text-center">
        <div className="small-caps mb-3 text-[#FF3366]">Was this helpful?</div>
        <p className="text-black/65 mb-5 max-w-lg mx-auto">If not, write to <a className="underline" href="mailto:support@sendme.co.in">support@sendme.co.in</a> — we read every email.</p>
        <Link to="/" className="btn-brut-primary">Back to SendMe <ArrowRight size={16} weight="bold"/></Link>
      </div>
    </article>
  );
}

function Block({ block }) {
  switch (block.type) {
    case "h2":
      return <h2 className="text-2xl md:text-3xl tracking-tight mt-10 mb-4">{block.text}</h2>;
    case "p":
      return <p className="text-[17px] leading-[1.75] text-black/80 mb-5">{block.text}</p>;
    case "list":
      return (
        <ul className="mb-5 space-y-2 pl-1">
          {block.items.map((it, i) => (
            <li key={i} className="flex gap-3 text-[16px] leading-relaxed text-black/80">
              <span className="text-[#FF3366] font-bold mt-0.5">→</span>
              <span>{it}</span>
            </li>
          ))}
        </ul>
      );
    default:
      return null;
  }
}
